import { hideLightingOverlay } from "../systems/lighting.js";
import { closeUI } from "../ui/memoryUI.js";
import { closeEscapeUI } from "../ui/escapeUI.js";
import { resetGameHud } from "../ui/gameHud.js";
import { areGameAssetsLoaded, startGameAssetLoad } from "../core/assets.js";
import { stopSplashDrawing } from "./splashLoading.js";
import { AUDIO_SOUNDS, playUi } from "../systems/audio.js";

const INTRO_RAM_COUNT = 4;
const INTRO_RAM_SCALE = 3;
const INTRO_RAM_SPACING = 78;
const INTRO_DIE_SHAKE_SECONDS = 0.7;
const INTRO_DIE_SHAKE_PIXELS = 5;
const INTRO_POST_KEY_BEAT = 0.55;
const INTRO_KILL_GAP = 0.85;
const INTRO_CENTER_MOVE_SECONDS = 0.55;
const INTRO_POST_CENTER_HOLD = 0.35;
const INTRO_TITLE = "PETA-BYTE";

function getRamBasePos(index) {
    const totalW = (INTRO_RAM_COUNT - 1) * INTRO_RAM_SPACING;
    return vec2(
        center().x - totalW / 2 + index * INTRO_RAM_SPACING,
        center().y + 12,
    );
}

function killRamStick(ram) {
    if (!ram?.exists?.() || ram.isDead) return;
    ram.isDead = true;
    ram.shakeElapsed = 0;
    playUi(AUDIO_SOUNDS.robotDeath);
}

function moveRamStickToCenter(ram) {
    if (!ram?.exists?.()) return;
    ram.isCentering = true;
    ram.centerElapsed = 0;
    ram.centerStartPos = vec2(ram.pos.x, ram.pos.y);
}

export function registerIntroScene() {
    scene("intro", (args = {}) => {
        stopSplashDrawing();
        hideLightingOverlay();
        resetGameHud();
        closeUI();
        closeEscapeUI();
        startGameAssetLoad();

        const canvas = document.querySelector("canvas");
        if (canvas) canvas.style.cursor = "default";

        const returning = !!args.returning;
        // Assets should already be ready from onLoading splash; still wait if not.
        let phase = (args.assetsReady || returning || areGameAssetsLoaded())
            ? "ready"
            : "loading";
        let canContinue = phase === "ready";
        let continuing = false;

        const bg = add([
            rect(width(), height()),
            pos(0, 0),
            color(0, 0, 0),
            fixed(),
        ]);

        const title = add([
            text(INTRO_TITLE, { font: "ShareTechMono", size: 28 }),
            pos(center().x, center().y - 64),
            anchor("center"),
            color(0, 255, 180),
            opacity(canContinue ? 1 : 0),
            fixed(),
            z(10),
        ]);

        const prompt = add([
            text("Press any key", { font: "ShareTechMono", size: 16 }),
            pos(center().x, center().y + 78),
            anchor("center"),
            color(255, 200, 100),
            opacity(canContinue ? 1 : 0),
            fixed(),
            z(10),
            { blink: 0 },
        ]);

        const ramSticks = Array.from({ length: INTRO_RAM_COUNT }, (_, index) => {
            const ram = add([
                sprite("ram_stick"),
                pos(getRamBasePos(index)),
                anchor("center"),
                scale(INTRO_RAM_SCALE),
                color(255, 255, 255),
                opacity(1),
                fixed(),
                z(2),
                {
                    basePos: getRamBasePos(index),
                    isDead: false,
                    shakeElapsed: INTRO_DIE_SHAKE_SECONDS,
                    isCentering: false,
                    centerElapsed: 0,
                    centerStartPos: null,
                },
            ]);

            ram.onUpdate(() => {
                ram.basePos = getRamBasePos(index);
                if (ram.isCentering) {
                    ram.centerElapsed += dt();
                    const t = Math.min(1, ram.centerElapsed / INTRO_CENTER_MOVE_SECONDS);
                    const target = center();
                    ram.pos = ram.centerStartPos.add(target.sub(ram.centerStartPos).scale(t));
                    return;
                }

                if (!ram.isDead) {
                    ram.pos = ram.basePos;
                    return;
                }

                ram.shakeElapsed += dt();
                const t = Math.min(1, ram.shakeElapsed / INTRO_DIE_SHAKE_SECONDS);
                const amount = INTRO_DIE_SHAKE_PIXELS * (1 - t);
                ram.pos = ram.basePos.add(vec2(
                    (Math.random() - 0.5) * amount * 2,
                    (Math.random() - 0.5) * amount * 2,
                ));
                const dim = Math.round(255 * (1 - t));
                ram.color = rgb(dim, dim, dim);
                if (t >= 1) {
                    ram.opacity = 0;
                }
            });

            return ram;
        });

        function layoutChrome() {
            title.pos = vec2(center().x, center().y - 64);
            prompt.pos = vec2(center().x, center().y + 78);
            for (let i = 0; i < ramSticks.length; i++) {
                if (!ramSticks[i]?.exists?.()) continue;
                ramSticks[i].basePos = getRamBasePos(i);
                if (!ramSticks[i].isDead && !ramSticks[i].isCentering) {
                    ramSticks[i].pos = ramSticks[i].basePos;
                }
            }
        }

        function showReady() {
            if (phase === "ready" || phase === "leaving" || phase === "dying") return;
            phase = "ready";
            title.opacity = 1;
            prompt.opacity = 1;
            canContinue = true;
        }

        function goToBoot() {
            go("boot", {
                autoStart: true,
                resetMemory: !returning,
                kernelOnlyMemory: !returning,
                openMemoryOnInit: false,
                respawnFromCheckpoint: false,
            });
        }

        function onContinuePressed() {
            if (!canContinue || continuing || phase !== "ready") return;
            continuing = true;
            canContinue = false;
            phase = "leaving";

            title.opacity = 0;
            prompt.opacity = 0;

            wait(INTRO_POST_KEY_BEAT, () => {
                phase = "dying";
                killRamStick(ramSticks[0]);

                wait(INTRO_KILL_GAP, () => {
                    killRamStick(ramSticks[1]);

                    wait(INTRO_KILL_GAP, () => {
                        killRamStick(ramSticks[2]);

                        wait(INTRO_DIE_SHAKE_SECONDS, () => {
                            moveRamStickToCenter(ramSticks[3]);
                            wait(INTRO_CENTER_MOVE_SECONDS + INTRO_POST_CENTER_HOLD, () => {
                                goToBoot();
                            });
                        });
                    });
                });
            });
        }

        onResize(() => {
            bg.width = width();
            bg.height = height();
            layoutChrome();
        });

        onUpdate(() => {
            if (phase === "loading" && areGameAssetsLoaded()) {
                showReady();
            }
            if (phase === "ready") {
                prompt.blink += dt();
                prompt.opacity = 0.45 + 0.55 * (0.5 + 0.5 * Math.sin(prompt.blink * 3.2));
            }
        });

        onKeyPress(onContinuePressed);
        // α.26+: bare onClick(fn) removed — use onMousePress (obj.onClick / onClick("tag") still OK)
        onMousePress(onContinuePressed);
        layoutChrome();
    });
}
